import alt from 'components/Dispatcher';
import immutableStore from 'utils/immutableStore';
import FileUploadActions from 'actions/files/FileUploadActions';
import {List, Map} from 'immutable';
import {associateBy} from 'utils/associateBy';
import LibraryActions from 'actions/library/LibraryActions';
import {Data, Int} from '@store/@types/util';
import {SearchResult, SearchResults} from 'records/Search';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {Vote} from 'records/CallDetails';
import {UserId} from 'records/User';
import {Store} from '@store/@types/alt/Store';

export interface TagData {
  file_upload_id: FileUploadId;
  legacy_placeholder: null;
  library_id: Int;
  library_name: string;
  tag_created: string;
  tag_name: string;
  tag_user_id: UserId;
  tag_user_name: string;
  user_avatar: string | null;
  user_color: string;
  user_first_name: string;
  user_last_name: string;
}

interface FileUploadStoreState {
  isFileUploadFetching: boolean;
  didFileUploadFetchFail: boolean;
  fileUploads: List<SearchResult>;
  fileUploadsMap: Map<FileUploadId, SearchResult>;
  isSearching: boolean;
  wasErrorSearching: boolean;
  callVotes: Map<FileUploadId, Vote>;
  callTags: Map<FileUploadId, List<TagData>>;
}

const State = Data<FileUploadStoreState>({
  isFileUploadFetching: false,
  didFileUploadFetchFail: false,
  fileUploads: List(),
  fileUploadsMap: Map(),
  isSearching: false,
  wasErrorSearching: false,
  callVotes: Map(), // Map<FileUploadId, Vote>
  callTags: Map(), // Map<FileUploadId, List<TagData>>
})();

@immutableStore
class FileUploadStore extends Store<typeof State> {
  constructor() {
    super();
    this.state = State;
    this.bindActions(FileUploadActions);
    this.bindActions(LibraryActions);
  }

  findFilesById() {
    this.setState({
      isSearching: true,
      wasErrorSearching: false,
    });
  }

  findFilesByIdSuccess(data: SearchResults) {
    const allUploads = this.state.fileUploads.concat(data.results);
    this.setState({
      fileUploads: allUploads,
      fileUploadsMap: this.state.fileUploadsMap.merge(Map(data.results.map(f => [f.id, f]))),
      isSearching: false,
      callVotes: associateBy(allUploads, f => f.id).map(f => f.callDetails?.vote),
    });
  }

  findFilesByIdError() {
    this.setState({
      isSearching: false,
      wasErrorSearching: true,
    });
  }

  updateVotes(results: List<SearchResult>) {
    const callVotes = associateBy(results, f => f.id).map(f => f.callDetails?.vote);
    this.setState({
      callVotes: this.state.callVotes.merge(callVotes),
    });
  }

  setVote({id, vote}) {
    this.setState({
      callVotes: this.state.callVotes.set(id, vote),
    });
  }

  /**
   * @link LibrarySource.getInfoAboutFiles
   */
  getInfoAboutFilesSuccess({data: {info}}) {
    this.setState({
      callTags: List(info as TagData[]).groupBy(i => i.file_upload_id),
    });
  }

  /**
   * @link LibrarySource.getFileInfo
   */
  getFileInfoSuccess({data: {file_upload_id, libraries}}) {
    this.setState({
      callTags: this.state.callTags.set(file_upload_id, List(libraries)),
    });
  }
}

export default alt.createStore(FileUploadStore);
